import { List } from 'immutable';
import { composeInitialState } from '@opengov/compose-app';
import { IRecordOf, RecordOf } from '@opengov/compose-app/immutable';
import { BasicColumnTypes } from '@opengov/ogql-types';
import {
  ConditionalReportColumnRecord,
  EConditionalComparisonOperators,
  EReportColumnTypes,
  IConditionalExpressionComparisonValue,
  IConstantReportColumn,
  IReportColumn,
  ReportColumnMetaDataRecord,
  StorageReportColumnRecord
} from '@opengov/reporting-types';
import { IConditionalReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';

import { composeStateMachine } from '../../../../module';
import { DataTableStatus } from '../../../reportExplorer/types';

export interface ICurrentConditionalSetupRecord<OutputColumnType extends string | number | Date> {
  operator?: EConditionalComparisonOperators;
  matchValue?: IReportColumn;
  lowerBound?: IReportColumn;
  upperBound?: IReportColumn;
  returnedValue?: IConditionalExpressionComparisonValue<OutputColumnType>;
}

export const CurrentConditionalSetupRecord = RecordOf<ICurrentConditionalSetupRecord<any>>({
  operator: null,
  matchValue: null,
  lowerBound: null,
  upperBound: null,
  returnedValue: ''
});

export const ConditionalInitialState = composeInitialState({
  conditional: ConditionalReportColumnRecord({
    name: '',
    displayName: '',
    type: BasicColumnTypes.Text,
    columnType: EReportColumnTypes.conditional,
    hidden: false,
    sourceColumn: StorageReportColumnRecord(),
    conditions: List(),
    else: null,
    metadata: ReportColumnMetaDataRecord()
  }),
  currentConditionSetup: CurrentConditionalSetupRecord({
    operator: null,
    matchValue: null,
    lowerBound: null,
    upperBound: null,
    returnedValue: ''
  }),
  conditionalPreviewTableStatus: DataTableStatus.Current,
  queryResult: null
});

export const conditionalState = composeStateMachine('conditional', ConditionalInitialState, (compose) => ({
  resetConditional: compose.payloadlessAction((state) => state.clear()),
  updateSelectedColumnType: compose.action((state, payload: BasicColumnTypes) =>
    state
      .update('conditional', (c) =>
        c.set('type', payload).set('sourceColumn', StorageReportColumnRecord()).set('else', null)
      )
      .update('currentConditionSetup', (cs) =>
        cs
          .set('operator', null)
          .set('matchValue', null)
          .set('lowerBound', null)
          .set('upperBound', null)
          .set('returnedValue', '')
      )
      .set('queryResult', null)
  ),
  updateIfValue: compose.action((state, payload) =>
    state
      .update('conditional', (c) => c.set('sourceColumn', payload).set('else', null))
      .update('currentConditionSetup', (cs) =>
        cs
          .set('operator', null)
          .set('matchValue', null)
          .set('lowerBound', null)
          .set('upperBound', null)
          .set('returnedValue', '')
      )
      .set('queryResult', null)
  ),

  updateCurrentConditionOperator: compose.action((state, payload) =>
    state.update('currentConditionSetup', (cs) => cs.set('operator', payload)).set('queryResult', null)
  ),
  updateCurrentConditionCustomValue: compose.action((state, payload) =>
    state
      .update('currentConditionSetup', (cs) =>
        cs.update(payload.valueType, (cv: IRecordOf<IConstantReportColumn<any>>) => cv.set('value', payload.value))
      )
      .set('queryResult', null)
  ),
  updateCurrentConditionMatchValue: compose.action((state, payload) =>
    state.update('currentConditionSetup', (cs) => cs.set('matchValue', payload)).set('queryResult', null)
  ),
  updateCurrentConditionLowerBound: compose.action((state, payload) =>
    state.update('currentConditionSetup', (cs) => cs.set('lowerBound', payload)).set('queryResult', null)
  ),
  updateCurrentConditionUpperBound: compose.action((state, payload) =>
    state.update('currentConditionSetup', (cs) => cs.set('upperBound', payload)).set('queryResult', null)
  ),
  updateCurrentConditionReturnedValue: compose.action((state, payload) =>
    state.update('currentConditionSetup', (cs) => cs.set('returnedValue', payload)).set('queryResult', null)
  ),
  updateElseResult: compose.action((state, payload) =>
    state.update('conditional', (c) => c.set('else', payload)).set('queryResult', null)
  ),
  updateColumnName: compose.action((state, payload: string) =>
    state.update('conditional', (c) => c.set('displayName', payload)).set('queryResult', null)
  ),
  updateConditionalPreviewTableStatus: compose.action((state, payload: DataTableStatus) =>
    state.set('conditionalPreviewTableStatus', payload)
  ),
  addCondition: compose.action((state, payload) => {
    const conditions = List<IConditionalReportColumn<string | number | Date>>().concat([payload]);
    return state.update('conditional', (c) => c.set('conditions', conditions));
  }),
  setQueryResult: compose.action((state, payload: any) => state.set('queryResult', payload))
}));

export const {
  actions: conditionalActions,
  actionCreators: conditionalActionCreators,
  selector: conditionalSelector,
  reducer: conditionalReducer
} = conditionalState;
